// Sticky behavior for .top-navigation-wrapper
// Attaches an IntersectionObserver to make the navigation stick to the top

const initTopNavigationSticky = () => {
  const nodes = document.querySelectorAll('.top-navigation-wrapper[sticky]');
  if (!nodes.length) return;

  // Find header component so we can react to its height & sticky state
  const header = document.querySelector('#header-component');

  nodes.forEach((el) => {
    const stickyMode = el.getAttribute('sticky'); // 'always' || 'scroll-up' etc.
    // @ts-ignore
    const always = stickyMode === 'always';

    // sentinel before the nav to detect when it crosses the top area
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    // @ts-ignore
    el.parentNode.insertBefore(sentinel, el);

    // helper to read current header height (px)
    const getHeaderHeight = () => {
      // prefer the header element height if present
      if (header && header instanceof HTMLElement) {
        return Math.round(header.getBoundingClientRect().height) || 0;
      }
      // fallback to CSS custom property
      const v = getComputedStyle(document.body).getPropertyValue('--header-height');
      return v ? Math.round(parseFloat(v)) : 0;
    };

    let headerHeight = getHeaderHeight() - 2;

    // create observer with dynamic rootMargin depending on header height
    // @ts-ignore
    let observer = null;
    const createObserver = () => {
      // @ts-ignore
      if (observer) observer.disconnect();

      const rootMargin = `-${headerHeight}px 0px 0px 0px`;
      const config = { root: null, rootMargin, threshold: 0 };

      observer = new IntersectionObserver(([entry]) => {
        if (!entry) return;

        // When the sentinel is out of view past the header bottom, make the nav sticky
        if (!entry.isIntersecting) {
          // stick it below header
          // @ts-ignore
          el.dataset.stickyState = 'active';
          el.classList.add('sticky');
          // @ts-ignore
          el.style.position = 'sticky';
          // @ts-ignore
          el.style.top = `${headerHeight}px`;
          // @ts-ignore
          el.style.zIndex = '98';
          // @ts-ignore
          document.body.style.setProperty('--top-navigation-height', `${el.offsetHeight}px`);
        } else {
          // back to normal flow
          // @ts-ignore
          el.dataset.stickyState = 'inactive';
          el.classList.remove('sticky');
          // @ts-ignore
          el.style.position = '';
          // @ts-ignore
          el.style.top = '';
          document.body.style.setProperty('--top-navigation-height', '0px');
        }
      }, config);

      observer.observe(sentinel);
    };

    createObserver();

    // Smooth-scroll anchor links with proper offset for header + sticky nav
    const anchorLinks = el.querySelectorAll('a[href^="#"]');
    anchorLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const id = link.getAttribute('href');
        if (!id || id === '#') return;
        const target = document.querySelector(id);
        if (!target) return;

        e.preventDefault();
        const navHeight = el.offsetHeight || 0;
        const offset = headerHeight + navHeight;
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        history.pushState(null, null, id);
      });
    });

    // Update navigation height on resize so other layout can use it
    const resizeObserver = new ResizeObserver(() => {
      if (el.classList.contains('sticky')) {
        // @ts-ignore
        document.body.style.setProperty('--top-navigation-height', `${el.offsetHeight}px`);
        // ensure top matches header height
        headerHeight = getHeaderHeight() - 2;
        // @ts-ignore
        el.style.top = `${headerHeight}px`;
      }
    });

    resizeObserver.observe(el);

    // If header exists, observe its size & attribute changes so the secondary nav
    // can adjust its rootMargin and offset when header changes height or sticky state.
    let headerAttrObserver = null;
    let headerResizeObserver = null;

    if (header) {
      headerResizeObserver = new ResizeObserver(() => {
        const newHeaderHeight = getHeaderHeight() - 2;
        if (newHeaderHeight !== headerHeight) {
          headerHeight = newHeaderHeight;
          // re-create observer with updated rootMargin so the trigger point follows header
          createObserver();
          // if nav already sticky, re-apply top offset
          // @ts-ignore
          if (el.classList.contains('sticky')) el.style.top = `${headerHeight}px`;
        }
      });

      headerResizeObserver.observe(header);

      headerAttrObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'data-sticky-state') {
            // header became sticky/inactive; header.js keeps --header-height up-to-date
            headerHeight = getHeaderHeight() - 2;
            // re-create observer to account for potential layout shift
            createObserver();

            if (el.classList.contains('sticky')) {
              // @ts-ignore
              el.style.top = `${headerHeight}px`;
            }
          }
        }
      });

      headerAttrObserver.observe(header, { attributes: true });
    }

    // MOBILE: Toggle mobile menu when #navigation-title is clicked
    // @ts-ignore
    let toggleMenu;
    // @ts-ignore
    let navTitleKeyHandler;
    // @ts-ignore
    let documentClickHandler;
    // @ts-ignore
    let escHandler;

    const navTitle = el.querySelector('#navigation-title');
    const navWrapper = el.querySelector('.navigation-bar__item-wrapper');

    if (navTitle && navWrapper) {
      // accessibility helpers
      navTitle.setAttribute('role', 'button');
      if (!navTitle.hasAttribute('tabindex')) navTitle.setAttribute('tabindex', '0');
      navTitle.setAttribute('aria-expanded', String(!navWrapper.classList.contains('active')));

      // @ts-ignore
      toggleMenu = (e) => {
        // Prevent document click handler from immediately closing it
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
        const isHidden = navWrapper.classList.toggle('active');
        navTitle.setAttribute('aria-expanded', String(!isHidden));
      };

      const closeMenu = () => {
        if (!navWrapper.classList.contains('active')) {
          navWrapper.classList.add('mobile-menu--hidden');
          navTitle.setAttribute('aria-expanded', 'false');
        }
      };

      // @ts-ignore
      documentClickHandler = (e) => {
        if (!el.contains(e.target)) closeMenu();
      };

      // @ts-ignore
      escHandler = (e) => {
        if (e.key === 'Escape') closeMenu();
      };

      // @ts-ignore
      navTitleKeyHandler = (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          // @ts-ignore
          toggleMenu(e);
        }
      };

      navTitle.addEventListener('click', toggleMenu);
      navTitle.addEventListener('keydown', navTitleKeyHandler);
      document.addEventListener('click', documentClickHandler);
      document.addEventListener('keydown', escHandler);
    }

    // Cleanup when element is removed from DOM
    const mo = new MutationObserver(() => {
      if (!document.body.contains(el)) {
        // @ts-ignore
        observer?.disconnect();
        resizeObserver.disconnect();
        headerResizeObserver?.disconnect();
        headerAttrObserver?.disconnect();
        // remove mobile listeners if present
        // @ts-ignore
        if (toggleMenu) {
          try {
            // @ts-ignore
            navTitle.removeEventListener('click', toggleMenu);
            // @ts-ignore
            navTitle.removeEventListener('keydown', navTitleKeyHandler);
          } catch (err) {}
          // @ts-ignore
          document.removeEventListener('click', documentClickHandler);
          // @ts-ignore
          document.removeEventListener('keydown', escHandler);
        }
        mo.disconnect();
      }
    });

    mo.observe(document, { childList: true, subtree: true });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTopNavigationSticky);
} else {
  initTopNavigationSticky();
}
